import {createContext, useContext, useMemo} from 'react';

/**
 * Theme context for HeatshrinkEditor federated component.
 * Provides site-specific styling and content configuration that can be
 * passed in from the host application.
 */
const ThemeContext = createContext(null);

/**
 * Default theme configuration (simplified).
 * Most fields are derived from core colors if not specified.
 * Host apps only need to override values that differ from defaults.
 */
export const defaultTheme = {
  name: 'Heatshrink Printing',

  colors: {
    // Core palette (these are the main colors to customize)
    primary: '#5a83a1',
    primaryDark: '#0a3257',

    // Navigation (navText defaults to primaryDark)
    navBackground: '#8bb8d1',
    navText: undefined,
    navActiveBackground: '#fff',

    // Buttons - primary (defaults to primaryDark, hover to primary)
    buttonPrimary: undefined,
    buttonPrimaryText: '#fff',
    buttonPrimaryHover: undefined,

    // Buttons - outline (border defaults to primary, text to primaryDark)
    buttonOutlineBackground: 'transparent',
    buttonOutlineBorder: undefined,
    buttonOutlineText: undefined,
    buttonOutlineHoverBackground: 'transparent',
    buttonOutlineHoverBorder: undefined,
  },

  content: {
    footer: null,
    disclaimer: null,
  },
};

/**
 * Compute derived color values from core palette.
 * Fills in undefined values with sensible defaults based on primary colors.
 */
function computeDerivedColors(colors) {
  const {primary, primaryDark} = colors;

  // Helper to create rgba from hex with opacity
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const navText = colors.navText ?? primaryDark;

  return {
    // Core - pass through
    primary,
    primaryDark,

    // Navigation
    navBackground: colors.navBackground ?? '#8bb8d1',
    navDivider: colors.navDivider ?? colors.navBackground ?? '#8bb8d1',
    navText,
    navTextDisabled: colors.navTextDisabled ?? hexToRgba(navText, 0.33),
    navActiveBackground: colors.navActiveBackground ?? '#fff',
    navActiveText: colors.navActiveText ?? navText,

    // General
    headerText: colors.headerText ?? primaryDark,
    borderColor: colors.borderColor ?? colors.navBackground ?? primary,
    modalHeaderBackground: colors.modalHeaderBackground ?? colors.navBackground ?? primary,

    // Primary button (bg/border combined)
    buttonPrimaryBackground: colors.buttonPrimary ?? primaryDark,
    buttonPrimaryBorder: colors.buttonPrimary ?? primaryDark,
    buttonPrimaryText: colors.buttonPrimaryText ?? '#fff',
    buttonPrimaryHoverBackground: colors.buttonPrimaryHover ?? primary,
    buttonPrimaryHoverBorder: colors.buttonPrimaryHover ?? primary,

    // Outline button
    buttonOutlineBackground: colors.buttonOutlineBackground ?? 'transparent',
    buttonOutlineBorder: colors.buttonOutlineBorder ?? primary,
    buttonOutlineText: colors.buttonOutlineText ?? primaryDark,
    buttonOutlineHoverBackground: colors.buttonOutlineHoverBackground ?? 'transparent',
    buttonOutlineHoverBorder: colors.buttonOutlineHoverBorder ?? primaryDark,
    buttonOutlineHoverText: colors.buttonOutlineHoverText ?? primaryDark,
  };
}

/**
 * Hook to access theme configuration.
 *
 * @returns {typeof defaultTheme} The current theme configuration
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

/**
 * Deep merge two objects, with source values overriding target values.
 * Only merges plain objects, not arrays.
 */
function deepMerge(target, source) {
  const result = {...target};
  for (const key in source) {
    if (source[key] !== null && source[key] !== undefined) {
      if (
        typeof source[key] === 'object' &&
        !Array.isArray(source[key]) &&
        typeof target[key] === 'object' &&
        !Array.isArray(target[key])
      ) {
        result[key] = deepMerge(target[key], source[key]);
      } else {
        result[key] = source[key];
      }
    }
  }
  return result;
}

/**
 * Provider component for theme context.
 * Merges provided theme with defaults and applies CSS variables.
 *
 * @param {Object} props
 * @param {Object} [props.theme] - Custom theme overrides
 * @param {React.ReactNode} props.children
 */
export function ThemeProvider({theme: customTheme, children}) {
  // Merge custom theme with defaults and compute derived colors
  const theme = useMemo(() => {
    const merged = customTheme ? deepMerge(defaultTheme, customTheme) : defaultTheme;
    // Compute derived colors from the simplified input
    const computedColors = computeDerivedColors(merged.colors);
    return {
      ...merged,
      colors: computedColors,
    };
  }, [customTheme]);

  // Generate CSS variables from computed theme colors
  const cssVariables = useMemo(() => {
    const c = theme.colors;
    return {
      '--hsp-color-primary': c.primary,
      '--hsp-color-primary-dark': c.primaryDark,
      '--hsp-nav-background': c.navBackground,
      '--hsp-nav-divider': c.navDivider,
      '--hsp-nav-text': c.navText,
      '--hsp-nav-text-disabled': c.navTextDisabled,
      '--hsp-nav-active-bg': c.navActiveBackground,
      '--hsp-nav-active-text': c.navActiveText,
      '--hsp-header-text': c.headerText,
      '--hsp-border-color': c.borderColor,
      '--hsp-modal-header-bg': c.modalHeaderBackground,
      '--hsp-btn-primary-bg': c.buttonPrimaryBackground,
      '--hsp-btn-primary-border': c.buttonPrimaryBorder,
      '--hsp-btn-primary-text': c.buttonPrimaryText,
      '--hsp-btn-primary-hover-bg': c.buttonPrimaryHoverBackground,
      '--hsp-btn-primary-hover-border': c.buttonPrimaryHoverBorder,
      '--hsp-btn-outline-bg': c.buttonOutlineBackground,
      '--hsp-btn-outline-border': c.buttonOutlineBorder,
      '--hsp-btn-outline-text': c.buttonOutlineText,
      '--hsp-btn-outline-hover-bg': c.buttonOutlineHoverBackground,
      '--hsp-btn-outline-hover-border': c.buttonOutlineHoverBorder,
      '--hsp-btn-outline-hover-text': c.buttonOutlineHoverText,
    };
  }, [theme.colors]);

  return (
    <ThemeContext.Provider value={theme}>
      <div className="hsp-theme-root" style={cssVariables}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export default ThemeContext;
